import config from '../../config';
import _toggleFilter from '../../shifts/components/filters/toggleFilter';
import { PREMIUM_FEATURE_NAMES } from '../../constants';

export default class ShiftTasksService {
  mode = 'day';
  query = '';
  range = null;
  hasEvents = false;

  constructor(
    Authentication,
    AlertService,
    PersonaCollection,
    EventCollection,
    SlingApi,
    Utils,
    sdk,
    toTimeFilter,
    $rootScope
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      AlertService,
      PersonaCollection,
      EventCollection,
      SlingApi,
      Utils,
      sdk,
      toTimeFilter,
      $rootScope,
    });

    this.canSeeUserShifts = sdk('process/canSeeUserShifts');
    this.TaskInstancesCollection = sdk('models/TaskInstancesCollection');
    Utils.extendWithProps(sdk('constants'), this, [
      'DEFAULT_TASK_INSTANCES_FILTERS',
      'TASK_INSTANCE_STATES',
      'DEFAULT_TASK_INSTANCES_GROUP_METHOD',
      'TASK_INSTANCES_GROUP_BY_NONE',
      'TASK_INSTANCES_GROUP_BY_LOCATION',
      'TASK_INSTANCES_GROUP_BY_POSITION',
      'TASK_INSTANCES_GROUP_BY_STATUS',
      'DEFAULT_TASK_INSTANCES_SORT_METHOD',
      'TASK_INSTANCES_SORT_BY_DATE',
      'TASK_INSTANCES_SORT_BY_STATUS',
      'TASK_INSTANCE_EVENT_TYPES',
    ]);
    this.sortMethod = this.DEFAULT_TASK_INSTANCES_SORT_METHOD;
    this.init();
  }

  init() {
    this.setReferenceDate();
    this.initClientCacheForTaskInstances();
    this.initPersonas();
    this.initTaskInstancesFilters();
    this.initGroupMethods();
    this.initSortMethods();
    this.initEventListeners();
  }

  get areOldTasksEnabled() {
    return config.OLD_TASKS_ENABLED;
  }

  get isFeatureEnabled() {
    return this.Authentication.isOrgFeatureEnabled(PREMIUM_FEATURE_NAMES.shiftTasks);
  }

  get isFeatureTurnedOn() {
    return !this.Authentication.user.orgSettings.tasksFeatureDisabled;
  }

  get oldTasksUsed() {
    if (!__STAGING__ && this.Authentication.user.org.id === 509370) {
      return true;
    }

    return this.Authentication.user.org.oldTasksUsed;
  }

  get clientcache() {
    return this.Authentication.user.clientcache;
  }

  get filtersOpened() {
    return this.clientcache.taskInstances?.filtersOpened;
  }

  set filtersOpened(value) {
    this.clientcache.taskInstances.filtersOpened = value;
    this.Authentication.syncClientCache();
  }

  async loadTaskInstances() {
    if (!this.range) {
      return null;
    }

    const rangeStart = this.range[0];
    const rangeEnd = this.range[this.range.length - 1];
    const realFromDate = this.Utils.getRealDate(rangeStart);
    const realToDate = this.Utils.addDays(rangeEnd, 1);
    const dateRange = this.Utils.getDatesRangeParam(realFromDate, realToDate, true);

    if (!this.TaskInstancesCollection.isRangeCached(dateRange)) {
      const taskInstances = await this.SlingApi.getTaskInstances({ dates: dateRange });
      this.taskInstances = taskInstances.filter((instance) => {
        const { id, isManager } = this.Authentication.user;
        const { available, assigneeId } = instance;
        if (!isManager && available && assigneeId !== id) {
          return false;
        }
        return true;
      });
      this.TaskInstancesCollection.setInstancesAndRange(this.taskInstances, dateRange);
    } else {
      this.TaskInstancesCollection.setRange(dateRange);
      this.taskInstances = [...this.TaskInstancesCollection.allInstances];
    }
    this.filterAndGroupTaskInstances();
  }

  setReferenceDate(date) {
    this.date = date || this.Utils.getNowDate();
    this.dateString = this.Utils.formatDate(this.date);
  }

  initClientCacheForTaskInstances() {
    if (!('taskInstances' in this.clientcache)) {
      (this.clientcache.taskInstances = {
        mode: 'day',
        date: this.dateString,
        filter: { ...this.DEFAULT_TASK_INSTANCES_FILTERS },
        filtersOpened: false,
        groupMethod: this.DEFAULT_TASK_INSTANCES_GROUP_METHOD,
        sortMethod: this.DEFAULT_TASK_INSTANCES_SORT_METHOD,
      }),
        this.Authentication.syncClientCache();
    }
  }

  initPersonas() {
    this.refreshPersonas();
    this.PersonaCollection.onChange(this.refreshPersonas);
  }

  initEventListeners() {
    this.$rootScope.$on('socket:taskInstance:sync', (event, taskInstanceBasics) => {
      this.clearCache();
      if (taskInstanceBasics.deleted) {
        this.removeInstanceFromConnectedShift(taskInstanceBasics);
      }
    });
    this.$rootScope.$on('socket:events:sync', () => this.clearCache());
    this.EventCollection.onChange(() => this.clearCache());
    this.Authentication.onOrgUpdate((org, oldOrg) => {
      if (org.subscriptionLevel !== oldOrg.subscriptionLevel) {
        this.clearCache();
      }
    });
  }

  refreshPersonas = () => {
    this.locations = this.PersonaCollection.scheduleLocations;
    this.positions = this.PersonaCollection.schedulePositions;
    this.userGroups = this.PersonaCollection.groups.filter((g) => !g.hiddenOnSchedule);
    this.statusOptions = [...this.TASK_INSTANCE_STATES];
    this.stateOptions = [...this.TASK_INSTANCE_EVENT_TYPES];
    this.users = this.refreshUsers();
  };

  refreshUsers = () => {
    const users = this.PersonaCollection.allUsers
      .filter(this.canSeeUserShifts)
      .filter((u) => u && !u.hiddenOnSchedule);

    return this.Utils.sortBy(users, 'fullname').map((user) => ({
      id: user.id,
      name: user.fullname,
      fullname: user.fullname,
    }));
  };

  initGroupMethods() {
    const service = this;
    this.groupMethod =
      this.clientcache.taskInstances?.groupMethod || this.DEFAULT_TASK_INSTANCES_GROUP_METHOD;

    this.groupMethodOptions = [
      { value: this.TASK_INSTANCES_GROUP_BY_NONE, label: 'None' },
      {
        value: this.TASK_INSTANCES_GROUP_BY_POSITION,
        label: 'Positions',
        get hidden() {
          return service.positions?.length < 2;
        },
      },
      {
        value: this.TASK_INSTANCES_GROUP_BY_LOCATION,
        label: 'Locations',
        get hidden() {
          return service.locations?.length < 2;
        },
      },
      { value: this.TASK_INSTANCES_GROUP_BY_STATUS, label: 'Status' },
    ];
  }

  initSortMethods() {
    this.sortMethod =
      this.clientcache.taskInstances?.sortMethod || this.DEFAULT_TASK_INSTANCES_SORT_METHOD;

    this.sortMethodOptions = [
      { value: this.TASK_INSTANCES_SORT_BY_DATE, label: 'Date' },
      { value: this.TASK_INSTANCES_SORT_BY_STATUS, label: 'Status' },
    ];
  }

  initTaskInstancesFilters() {
    const { taskInstances: cached } = this.clientcache;
    if (cached.mode) {
      this.mode = cached.mode;
    }

    if (this.clientcache.taskInstances.filter) {
      this.filter = { ...this.clientcache.taskInstances.filter };
      this.checkFilterIntegrity();
    } else {
      this.filter = { ...this.DEFAULT_TASK_INSTANCES_FILTERS };
    }

    this.areFiltersEmpty = this.checkIfFiltersAreEmpty();
  }

  checkFilterIntegrity() {
    Object.keys(this.DEFAULT_TASK_INSTANCES_FILTERS).forEach((filterKey) => {
      if (!(filterKey in this.filter)) {
        this.filter[filterKey] = this.DEFAULT_TASK_INSTANCES_FILTERS[filterKey];
      }
    });

    Object.keys(this.filter).forEach((filterKey) => {
      if (!(filterKey in this.DEFAULT_TASK_INSTANCES_FILTERS)) {
        delete this.filter[filterKey];
      }
    });
  }

  checkIfFiltersAreEmpty() {
    return JSON.stringify(this.DEFAULT_TASK_INSTANCES_FILTERS) === JSON.stringify(this.filter);
  }

  onFilterClear() {
    this.areFiltersEmpty = this.checkIfFiltersAreEmpty();
    this.filterAndGroupTaskInstances();
  }

  changeGroupMethod(method) {
    this.groupMethod = method;
    this.clientcache.taskInstances.groupMethod = method;
    this.Authentication.syncClientCache();
    this.filterAndGroupTaskInstances();
  }

  changeSortMethod(method) {
    this.sortMethod = method;
    this.clientcache.taskInstances.sortMethod = method;
    this.Authentication.syncClientCache();
    this.filterAndGroupTaskInstances();
  }

  toggleFilter(...args) {
    _toggleFilter(this, ...args);
    this.areFiltersEmpty = this.checkIfFiltersAreEmpty();
    this.clientcache.taskInstances.filter = this.filter;
    this.Authentication.syncClientCache();
    this.filterAndGroupTaskInstances();
  }

  filterAndGroupTaskInstances() {
    this.filteredTaskInstances = this.TaskInstancesCollection.filterInstances(
      this.filter,
      this.query
    );
    this.groupedTaskInstances = this.TaskInstancesCollection.groupInstances(
      this.filteredTaskInstances,
      this.groupMethod,
      this.sortMethod
    );
    this.hasEvents = Object.keys(this.groupedTaskInstances).some(
      (key) => this.groupedTaskInstances[key].length
    );
  }

  clearAllFilters() {
    this.filter = { ...this.DEFAULT_TASK_INSTANCES_FILTERS };
    this.clientcache.taskInstances.filter = this.filter;
    this.Authentication.syncClientCache();
    this.areFiltersEmpty = this.checkIfFiltersAreEmpty();
    this.filterAndGroupTaskInstances();
  }

  search(searchText = '') {
    this.query = searchText.toLowerCase();
    this.filterAndGroupTaskInstances();
  }

  clearSearchQuery() {
    this.query = '';
    this.filterAndGroupTaskInstances();
  }

  async remove(taskInstance) {
    await this.SlingApi.deleteTaskInstance(taskInstance.id);
    this.TaskInstancesCollection.remove(taskInstance.id);
    this.filterAndGroupTaskInstances();
    this.removeInstanceFromConnectedShift(taskInstance);
    this.AlertService.info('Task successfully deleted');
  }

  async updateStatus(taskInstanceId, status) {
    await this.SlingApi.updateTaskInstanceStatus(taskInstanceId, status);
    this.TaskInstancesCollection.updateStatus(taskInstanceId, status);
    this.filterAndGroupTaskInstances();
  }

  async loadTaskInstanceDetails(taskInstanceId) {
    let instance = this.TaskInstancesCollection.findById(taskInstanceId);
    if (!instance) {
      instance = await this.SlingApi.getTaskInstanceDetails(taskInstanceId);
      this.TaskInstancesCollection.updateSingleInstance(instance);
    }

    if (!instance) {
      this.AlertService.danger("Could not find the task you're looking for");
    }
    return instance;
  }

  async updateSubtaskStatus(taskInstanceId, subtaskId, status) {
    await this.SlingApi.updateTaskInstanceSubtaskStatus(subtaskId, status);
    this.TaskInstancesCollection.updateSubtaskStatus(taskInstanceId, subtaskId, status);
    this.filterAndGroupTaskInstances();
  }

  async editTaskInstance(taskInstance) {
    const preparedTaskInstance = this.prepareTaskInstanceForSave(taskInstance);
    const result = await this.SlingApi.editTaskInstance(preparedTaskInstance);
    this.onEditTaskInstanceSuccess(result);
  }

  onEditTaskInstanceSuccess(taskInstance) {
    this.TaskInstancesCollection.edit(taskInstance);
    this.filterAndGroupTaskInstances();
  }

  prepareTaskInstanceForSave(taskInstance) {
    if (taskInstance.subtasks?.length) {
      return {
        ...taskInstance,
        subtasks: taskInstance.subtasks
          .map((subtask) => {
            if (!subtask.title) {
              return { ...subtask, deleted: true };
            }
            return subtask;
          })
          .filter(({ id, deleted }) => id || !deleted),
      };
    }

    return taskInstance;
  }

  getAssigneeLabel(taskInstance) {
    if (!taskInstance) {
      return '';
    }

    let assignee = 'No assignee';
    if (taskInstance.assigneeId) {
      assignee = this.PersonaCollection.personasDict[taskInstance.assigneeId]?.fullname;
    }
    return assignee;
  }

  getTimeLabel(taskInstance) {
    if (!taskInstance) {
      return '';
    }
    const dtstart = this.Utils.getRealDate(taskInstance.dtstart);
    const dtend = this.Utils.getRealDate(taskInstance.dtend);
    const endDescription = taskInstance.openEnd ? '' : ` - ${this.toTimeFilter(dtend)}`;
    return `${this.toTimeFilter(dtstart)}${endDescription}`;
  }

  getShiftDisplayLabel(taskInstance) {
    const assignee = this.getAssigneeLabel(taskInstance);
    const time = this.getTimeLabel(taskInstance);
    return `${assignee} • ${time}`;
  }

  clearCache() {
    this.TaskInstancesCollection.clearCache();
    this.$rootScope.$emit('shiftTasks:cache:cleared');
  }

  // @sideEffects: shifts in EventCollection might get mutatated when using this method
  removeInstanceFromConnectedShift(taskInstance) {
    const shift = this.EventCollection.allEvents?.find(
      (event) => event.id === taskInstance.eventId
    );
    if (shift) {
      const { taskInstances, taskTemplates } = shift;

      if (taskInstances) {
        shift.taskInstances = taskInstances.filter((instance) => instance.id !== taskInstance.id);
      }

      if (taskTemplates) {
        shift.taskTemplates = taskTemplates.filter(
          (template) => template.id !== taskInstance.taskTemplateId
        );
      }
    }
  }

  maybeAddTaskInstancesToCopy(shift) {
    if (!shift.taskInstancesToCopy && this.isFeatureEnabled && !shift.id && shift.taskInstances) {
      shift.taskInstancesToCopy = shift.taskInstances
        .filter(({ deleted }) => !deleted)
        .map(({ id }) => id);
    }
  }
}
